/**
 * ResultsDashboard.jsx
 * Dashboard-style results: 4 metric cards strip + recommended card + alternatives.
 * Replaces WizardResults.jsx.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Fade,
  Link,
  Tooltip,
  TextField,
  Collapse,
} from '@mui/material';
import {
  Replay as ReplayIcon,
  Share as ShareIcon,
  Calculate as CalculateIcon,
  TrendingDown as RateIcon,
  Payments as PaymentIcon,
  Shield as ShieldIcon,
  AccountBalance as AffordIcon,
  LocationOn as LocationOnIcon,
  Home as HomeIcon,
  SavingsOutlined as SavingsIcon,
  PersonOutline as PersonIcon,
  AccountBalanceWallet as WalletIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ResultCard from './ResultCard';
import { encodeWizardParams } from './wizardQuerySync';
import { trackWizardCtaClick } from './analytics';
import { formatCurrencyShort, getMortgageInsuranceRate } from '../mortgageCalcEngine';
import { useAnimatedNumber } from './useAnimatedNumber';
import { MORTGAGE_RATES } from '../../../data/mortgageRates';

/* ── Animated metric display ── */
const AnimatedMetricValue = ({ value, prefix = '', suffix = '', decimals = 2 }) => {
  const animated = useAnimatedNumber(value, 500);
  return (
    <Typography
      sx={{
        fontSize: { xs: '24px', md: '28px' }, fontWeight: 800,
        color: '#0F1E3A', lineHeight: 1, fontFeatureSettings: "'tnum'",
        letterSpacing: '-0.03em',
      }}
    >
      {prefix}{decimals > 0 ? animated.toFixed(decimals) : Math.round(animated).toLocaleString('en-CA')}{suffix}
    </Typography>
  );
};

/* ── Metric Card ── */
const MetricCard = ({ icon, label, children, accentColor = '#1E3A5F', elevated = false }) => (
  <Box
    sx={{
      p: 2.5, borderRadius: '14px', bgcolor: '#FFFFFF',
      border: 'none',
      borderTop: `3px solid ${accentColor}`,
      boxShadow: elevated
        ? '0 4px 16px rgba(0,0,0,0.06)'
        : '0 1px 4px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column', gap: 1,
      height: '100%',
      transition: 'all 0.2s ease',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {icon}
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
    </Box>
    {children}
  </Box>
);

/* ── P0.1: Collapsible rate difference explainer ── */
const RateExplainer = ({ userRate, brokerRate, province, price, dpPercent, term }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const labels = [
    { key: 'labelCmhc', text: 'cmhc' },
    { key: 'labelLtv', text: 'ltv' },
    { key: 'labelBroker', text: 'broker' },
    { key: 'labelProvince', text: 'province' },
    { key: 'labelType', text: 'type' },
  ];
  return (
    <Box
      sx={{
        bgcolor: '#F7F9FC', borderRadius: '12px', mb: 3,
        border: '1px solid #EEF0F3', overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: '#EEF0F3' }, transition: 'background-color 0.15s ease',
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ fontSize: 16, color: '#1E3A5F' }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>
            {t('mortgageRates.rateExplainer.title')}
          </Typography>
        </Box>
        {open ? <ExpandLessIcon sx={{ fontSize: 20, color: '#5F6B7A' }} /> : <ExpandMoreIcon sx={{ fontSize: 20, color: '#5F6B7A' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7, mb: 1.5 }}>
            {t('mortgageRates.rateExplainer.intro', { rate: brokerRate })}
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {labels.map(({ key, text }) => (
              <Typography key={key} component="li" sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7, mb: 0.5 }}>
                <strong>{t(`mortgageRates.rateExplainer.${key}`)}</strong> {t(`mortgageRates.rateExplainer.${text}`)}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ fontSize: '13px', color: '#1A1A1A', fontWeight: 600, mt: 1.5, lineHeight: 1.7 }}>
            {t('mortgageRates.rateExplainer.conclusion', {
              rate: userRate,
              province: province || 'Canada',
              price: price ? `$${price.toLocaleString('en-CA')}` : '—',
              dp: dpPercent.toFixed(0),
              term,
            })}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 1.5 }}>
            <Link
              href="#tasas-quebec"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('tasas-quebec')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t('mortgageRates.rateExplainer.brokerLink', { rate: brokerRate })}
            </Link>
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

const ResultsDashboard = ({ results, answers, onReset, onChangeAnswers }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lng } = useParams();
  const { recommended, alternatives, warnings, eligibility } = results;
  const [inlineIncome, setInlineIncome] = useState('');
  const [inlineDebts, setInlineDebts] = useState('');
  const [inlineMax, setInlineMax] = useState(null);

  if (!recommended) return null;

  // Derived values
  const dpPercent = answers.price > 0 ? (answers.downPayment / answers.price) * 100 : 0;
  const cmhcRate = getMortgageInsuranceRate(dpPercent);
  const maxAffordability = answers.income ? answers.income * 4.5 : 0;

  // Inline affordability calculator (GDS/TDS simplified)
  const handleCalcMax = () => {
    const income = parseFloat(inlineIncome) || 0;
    const debts = parseFloat(inlineDebts) || 0;
    if (income <= 0) return;
    const contractRate = (MORTGAGE_RATES?.canadaBestRates?.fiveYearFixed?.rate || 5.25) / 100;
    const stressRate = Math.max(contractRate + 0.02, 0.0525);
    const qualifyRate = stressRate / 12; // stress test: max(contract+2%, 5.25%)
    const amortMonths = 25 * 12;
    const factor = (1 - Math.pow(1 + qualifyRate, -amortMonths)) / qualifyRate;
    const maxPayment = (income * 0.39 / 12) - debts - 450; // 39% GDS minus debts and taxes estimate
    const max = Math.max(0, Math.round(maxPayment * factor));
    setInlineMax(max);
  };

  // Profile pills
  const pills = [];
  if (answers.province) pills.push({ icon: <LocationOnIcon sx={{ fontSize: 14 }} />, label: answers.province });
  if (answers.flow === 'buy' && answers.price) pills.push({ icon: <HomeIcon sx={{ fontSize: 14 }} />, label: formatCurrencyShort(answers.price) });
  if (answers.flow === 'buy' && answers.downPayment && answers.price) {
    pills.push({ icon: <SavingsIcon sx={{ fontSize: 14 }} />, label: `${dpPercent.toFixed(0)}% DP` });
  }
  if (answers.status) pills.push({ icon: <PersonIcon sx={{ fontSize: 14 }} />, label: t(`mortgageWizard.fields.statusOptions.${answers.status}`) });
  if (answers.flow === 'refi' && answers.balance) pills.push({ icon: <WalletIcon sx={{ fontSize: 14 }} />, label: formatCurrencyShort(answers.balance) });

  const handleShareLink = () => {
    const params = encodeWizardParams(answers);
    const url = `${window.location.origin}${window.location.pathname}?${params}`;
    navigator.clipboard.writeText(url).catch(() => {});
    trackWizardCtaClick('share_link', '');
  };

  const handleCalcClick = () => {
    trackWizardCtaClick('calculate', '');
    navigate(`/${lng || 'es'}/calculadora-hipoteca`);
  };

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Heading */}
        <Typography
          variant="h4"
          component="h2"
          tabIndex={-1}
          sx={{ fontWeight: 800, color: '#0F1E3A', mb: 2, fontSize: { xs: '20px', md: '24px' } }}
        >
          {t('mortgageWizard.results.title')}
        </Typography>

        {/* Profile pills + Start Over */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, flex: 1 }}>
            {pills.map((pill, i) => (
              <Chip
                key={i}
                icon={pill.icon}
                label={pill.label}
                size="small"
                sx={{
                  height: 28, fontSize: '12px', fontWeight: 600,
                  bgcolor: '#F7F9FC', color: '#5F6B7A', border: '1px solid #E6E8EC',
                  '& .MuiChip-icon': { color: '#5F6B7A' },
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#EEF0F3', borderColor: '#1E3A5F', color: '#1E3A5F', '& .MuiChip-icon': { color: '#1E3A5F' } },
                }}
              />
            ))}
          </Box>
          <Button
            variant="outlined"
            startIcon={<ReplayIcon sx={{ fontSize: '16px' }} />}
            onClick={onReset}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              borderColor: '#EEF0F3', color: '#5F6B7A', flexShrink: 0,
              '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1E3A5F', color: '#1E3A5F' },
            }}
          >
            {t('mortgageWizard.nav.reset')}
          </Button>
        </Box>

        {/* ── Metric Cards Strip ── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <MetricCard
              icon={<RateIcon sx={{ fontSize: 18, color: '#1E3A5F' }} />}
              label={t('mortgageEngine.metrics.bestRate')}
              accentColor="#1E3A5F"
              elevated
            >
              <AnimatedMetricValue value={recommended.rate} suffix="%" />
            </MetricCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetricCard
              icon={<PaymentIcon sx={{ fontSize: 18, color: '#5F6B7A' }} />}
              label={t('mortgageEngine.metrics.estPayment')}
              accentColor="#5F6B7A"
            >
              {recommended.payment > 0 ? (
                <AnimatedMetricValue value={recommended.payment} prefix="$" suffix="" decimals={0} />
              ) : (
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#C4C9D2' }}>—</Typography>
              )}
              {recommended.payment > 0 && (
                <Typography sx={{ fontSize: '12px', color: '#5F6B7A' }}>{t('mortgageRates.perMonthEst')}</Typography>
              )}
            </MetricCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetricCard
              icon={<ShieldIcon sx={{ fontSize: 18, color: cmhcRate > 0 ? '#F9A825' : '#5F6B7A' }} />}
              label={t('mortgageEngine.metrics.cmhc')}
              accentColor={cmhcRate > 0 ? '#F9A825' : '#5F6B7A'}
            >
              {answers.flow === 'buy' && answers.price > 0 ? (
                <Tooltip
                  title={cmhcRate > 0
                    ? t('mortgageRates.cmhcTooltip.applies', { rate: cmhcRate })
                    : t('mortgageRates.cmhcTooltip.notApplicable')}
                  arrow
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '22px', md: '26px' }, fontWeight: 800,
                      color: cmhcRate > 0 ? '#C68A00' : '#5F6B7A', lineHeight: 1,
                      fontFeatureSettings: "'tnum'",
                      cursor: 'help',
                    }}
                  >
                    {cmhcRate > 0 ? `${cmhcRate}%` : t('mortgageEngine.metrics.notApplicable')}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#C4C9D2' }}>
                  {t('mortgageEngine.metrics.notApplicable')}
                </Typography>
              )}
            </MetricCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetricCard
              icon={<AffordIcon sx={{ fontSize: 18, color: '#5F6B7A' }} />}
              label={t('mortgageEngine.metrics.maxAfford')}
              accentColor="#5F6B7A"
            >
              {maxAffordability > 0 ? (
                <AnimatedMetricValue value={maxAffordability} prefix="$" suffix="" decimals={0} />
              ) : inlineMax !== null ? (
                <>
                  <Typography
                    sx={{
                      fontSize: { xs: '22px', md: '26px' }, fontWeight: 800,
                      color: '#0F1E3A', lineHeight: 1, fontFeatureSettings: "'tnum'",
                    }}
                  >
                    ${inlineMax.toLocaleString('en-CA')}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 0.5 }}>
                    {t('mortgageRates.inlineCalc.basedOn', { income: parseFloat(inlineIncome).toLocaleString('en-CA'), debts: parseFloat(inlineDebts || 0).toLocaleString('en-CA') })}
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  <TextField
                    size="small"
                    type="number"
                    placeholder={t('mortgageRates.inlineCalc.annualIncome')}
                    value={inlineIncome}
                    onChange={(e) => setInlineIncome(e.target.value)}
                    InputProps={{ sx: { fontSize: '12px', height: 28 } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    placeholder={t('mortgageRates.inlineCalc.monthlyDebts')}
                    value={inlineDebts}
                    onChange={(e) => setInlineDebts(e.target.value)}
                    InputProps={{ sx: { fontSize: '12px', height: 28 } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleCalcMax}
                    disabled={!inlineIncome}
                    sx={{
                      bgcolor: '#1E3A5F', textTransform: 'none', fontSize: '12px',
                      fontWeight: 600, borderRadius: '6px', py: 0.25, minHeight: 26,
                      '&:hover': { bgcolor: '#1A3152' },
                    }}
                  >
                    {t('mortgageRates.inlineCalc.calculate')}
                  </Button>
                </Box>
              )}
            </MetricCard>
          </Grid>
        </Grid>

        {/* P0.1: Why your rate may differ — collapsible explainer */}
        {(() => {
          const brokerRate = MORTGAGE_RATES.quebec.bestRates.find((r) => r.term === '5 años fijo')?.rate;
          if (!brokerRate || recommended.rate <= brokerRate) return null;
          return (
            <RateExplainer
              userRate={recommended.rate}
              brokerRate={brokerRate}
              province={answers.province}
              price={answers.price}
              dpPercent={dpPercent}
              term={recommended.term || '5 años fijo'}
            />
          );
        })()}

        {/* Warnings */}
        {warnings.length > 0 && (
          <Box
            sx={{
              bgcolor: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '12px',
              p: 2, mb: 3,
            }}
          >
            {warnings.map((key) => (
              <Typography key={key} sx={{ fontSize: '13px', color: '#7D5A00', lineHeight: 1.6, mb: 0.5 }}>
                {t(`mortgageWizard.warnings.${key.replace('warning.', '')}`, { defaultValue: key })}
              </Typography>
            ))}
          </Box>
        )}

        {/* ── Recommended Card (full width) ── */}
        <Box sx={{ mb: 3 }}>
          <ResultCard result={recommended} isPrimary />
        </Box>

        {/* ── Alternatives ── */}
        {alternatives.length > 0 && (
          <>
            <Typography
              sx={{ fontSize: '14px', fontWeight: 600, color: '#5F6B7A', mb: 2, textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              {t('mortgageWizard.results.alternatives')}
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {alternatives.map((alt) => (
                <Grid item xs={12} sm={6} md={4} key={alt.bankName}>
                  <ResultCard result={alt} isPrimary={false} />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* What this means */}
        <Box sx={{ bgcolor: '#F7F9FC', borderRadius: '14px', p: 3, mb: 3 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', mb: 1.5 }}>
            {t('mortgageWizard.results.whatThisMeans')}
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {eligibility.insuredEligible && answers.price > 0 && (
              <Typography component="li" sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7 }}>
                {t('mortgageWizard.explanation.insured', {
                  dp_percent: dpPercent.toFixed(0),
                  price: formatCurrencyShort(answers.price),
                })}
              </Typography>
            )}
            {!eligibility.insuredEligible && answers.price > 0 && answers.downPayment > 0 && (
              <Typography component="li" sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7 }}>
                {t('mortgageWizard.explanation.uninsured', {
                  dp_percent: dpPercent.toFixed(0),
                })}
              </Typography>
            )}
            {eligibility.newcomerProgramLikely && (
              <Typography component="li" sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7 }}>
                {t('mortgageWizard.explanation.newcomerEligible', {
                  status: t(`mortgageWizard.fields.statusOptions.${answers.status}`),
                })}
              </Typography>
            )}
            {eligibility.needsAdvisor && (
              <Typography component="li" sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7 }}>
                {t('mortgageWizard.explanation.advisorRecommended')}
              </Typography>
            )}
            {answers.income > 0 && (
              <Typography component="li" sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.7 }}>
                {t('mortgageWizard.explanation.affordability', {
                  income: formatCurrencyShort(answers.income),
                  max_price: formatCurrencyShort(answers.income * 4.5),
                })}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Action CTA */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<CalculateIcon />}
            onClick={handleCalcClick}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              borderColor: '#1E3A5F', color: '#1E3A5F',
              '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1A3152' },
            }}
          >
            {t('mortgageWizard.results.calculatePayments')}
          </Button>
        </Box>

        {/* Bottom actions */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 2 }}>
          <Button
            size="small"
            onClick={onChangeAnswers}
            sx={{ textTransform: 'none', color: '#5F6B7A', fontSize: '13px', fontWeight: 500 }}
          >
            {t('mortgageWizard.results.changeAnswers')}
          </Button>
          <Button
            size="small"
            startIcon={<ShareIcon sx={{ fontSize: '14px' }} />}
            onClick={handleShareLink}
            sx={{ textTransform: 'none', color: '#5F6B7A', fontSize: '13px', fontWeight: 500 }}
          >
            {t('mortgageWizard.nav.shareLink')}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default ResultsDashboard;
